import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { useData } from '../context/DataContext';
import SafeIcon from '../common/SafeIcon';
import ShareModal from './ShareModal';
import KeyPhrasesInput from './KeyPhrasesInput';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiPlus, FiEdit2, FiTrash2, FiShare2, FiFileText, FiExternalLink, FiSearch, FiTarget, FiMessageCircle } = FiIcons;

const WebsiteDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { websites, addPage, deletePage } = useData();
  const [showAddForm, setShowAddForm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    url: '',
    content: '',
    keyPhrases: [],
    meta_description: '',
    ranking: '',
    social_media_caption: '',
    social_media_image_url: '',
    social_media_platforms: ['Facebook', 'Instagram', 'LinkedIn'],
    social_media_created: false,
    social_media_notes: ''
  });

  const website = websites.find(w => w.id === id);

  if (!website) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Website not found</h2>
          <button
            onClick={() => navigate('/admin')}
            className="bg-red-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-red-700 transition-colors text-sm sm:text-base"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await addPage(id, formData);
      setFormData({
        title: '',
        url: '',
        content: '',
        keyPhrases: [],
        meta_description: '',
        ranking: '',
        social_media_caption: '',
        social_media_image_url: '',
        social_media_platforms: ['Facebook', 'Instagram', 'LinkedIn'],
        social_media_created: false,
        social_media_notes: ''
      });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error adding page:', error);
    }
  };

  const handleDelete = async (pageId) => {
    if (window.confirm('Are you sure you want to delete this page?')) {
      try {
        await deletePage(id, pageId);
      } catch (error) {
        console.error('Error deleting page:', error);
      }
    }
  };

  const totalKeyPhrases = website.pages?.reduce((sum, page) => sum + (page.keyPhrases?.length || 0), 0) || 0;
  const rankedPages = website.pages?.filter(page => page.ranking).length || 0;
  const socialMediaReady = website.pages?.filter(page => page.social_media_created).length || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Enhanced Header */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-8 lg:mb-12 space-y-4 lg:space-y-0">
          <div className="flex items-center space-x-3 sm:space-x-6 w-full lg:w-auto">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 sm:p-3 text-gray-600 hover:bg-gray-200 rounded-xl transition-colors flex-shrink-0"
            >
              <SafeIcon icon={FiArrowLeft} className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
            <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
              {website.logo ? (
                <img
                  src={website.logo}
                  alt={website.name}
                  className="h-12 w-12 sm:h-14 sm:w-14 lg:h-16 lg:w-16 rounded-xl object-cover shadow-lg flex-shrink-0"
                />
              ) : (
                <div className="h-12 w-12 sm:h-14 sm:w-14 lg:h-16 lg:w-16 bg-gradient-to-br from-red-100 to-red-200 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                  <SafeIcon icon={FiFileText} className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-red-600" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent truncate">
                  {website.name}
                </h1>
                <p className="text-gray-600 text-sm sm:text-base lg:text-lg mt-1">{website.pages?.length || 0} pages being optimized</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 w-full lg:w-auto">
            <button
              onClick={() => setShowShareModal(true)}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center justify-center space-x-3 shadow-lg text-sm sm:text-base"
            >
              <SafeIcon icon={FiShare2} className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Share Project</span>
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full sm:w-auto bg-gradient-to-r from-red-600 to-red-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:from-red-700 hover:to-red-800 transition-all duration-200 flex items-center justify-center space-x-3 shadow-lg text-sm sm:text-base"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Add Page</span>
            </button>
          </div>
        </div>

        {/* Enhanced SEO Overview Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4 lg:gap-6 mb-8 sm:mb-10 lg:mb-12">
          <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6 border border-gray-200 col-span-2 sm:col-span-1">
            <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiFileText} className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              </div>
              <span className="font-semibold text-gray-700 text-xs sm:text-sm">Total Pages</span>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">{website.pages?.length || 0}</p>
          </div>

          <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6 border border-gray-200 col-span-2 sm:col-span-1">
            <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiSearch} className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
              </div>
              <span className="font-semibold text-gray-700 text-xs sm:text-sm">Key Phrases</span>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">{totalKeyPhrases}</p>
          </div>

          <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6 border border-gray-200 col-span-2 sm:col-span-1">
            <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiTarget} className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              </div>
              <span className="font-semibold text-gray-700 text-xs sm:text-sm">Ranked</span>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">{rankedPages}</p>
          </div>

          <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6 border border-gray-200 col-span-2 sm:col-span-1">
            <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiMessageCircle} className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
              </div>
              <span className="font-semibold text-gray-700 text-xs sm:text-sm">Social Ready</span>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">{socialMediaReady}</p>
          </div>

          <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6 border border-gray-200 col-span-2 sm:col-span-1">
            <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiTarget} className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
              </div>
              <span className="font-semibold text-gray-700 text-xs sm:text-sm">Success Rate</span>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">
              {website.pages?.length > 0 ? Math.round((rankedPages / website.pages.length) * 100) : 0}%
            </p>
          </div>
        </div>

        {/* Enhanced Add Page Form */}
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl lg:rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8 border border-gray-200"
          >
            <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiPlus} className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Add New Page</h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                    Page Title
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                    placeholder="Enter page title"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                    Page URL
                  </label>
                  <input
                    type="url"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                    placeholder="https://example.com/page"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                  Page Content & Description
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 h-24 sm:h-32 text-sm sm:text-base resize-none"
                  placeholder="Describe the page content and SEO focus..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                  Target Key Phrases & Search Terms
                </label>
                <KeyPhrasesInput
                  keyPhrases={formData.keyPhrases}
                  onChange={(phrases) => setFormData({ ...formData, keyPhrases: phrases })}
                  placeholder="Enter key phrase (e.g., best pizza delivery)"
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                    Meta Description
                  </label>
                  <textarea
                    value={formData.meta_description}
                    onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 h-20 sm:h-24 text-sm sm:text-base resize-none"
                    placeholder="Write a compelling meta description..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                    Current Ranking
                  </label>
                  <input
                    type="text"
                    value={formData.ranking}
                    onChange={(e) => setFormData({ ...formData, ranking: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                    placeholder="e.g., #1, #5, Page 2"
                  />
                </div>
              </div>

              {/* Social Media Section */}
              <div className="border-t border-gray-200 pt-4 sm:pt-6">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4 flex items-center space-x-2">
                  <SafeIcon icon={FiShare2} className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                  <span>Social Media Content (Optional)</span>
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                      Social Media Caption
                    </label>
                    <textarea
                      value={formData.social_media_caption}
                      onChange={(e) => setFormData({ ...formData, social_media_caption: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 h-24 sm:h-32 text-sm sm:text-base resize-none"
                      placeholder="Create an engaging caption for social media promotion..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                      Social Media Image URL
                    </label>
                    <input
                      type="url"
                      value={formData.social_media_image_url}
                      onChange={(e) => setFormData({ ...formData, social_media_image_url: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                      placeholder="https://example.com/social-image.jpg"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                    Social Media Notes
                  </label>
                  <textarea
                    value={formData.social_media_notes}
                    onChange={(e) => setFormData({ ...formData, social_media_notes: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 h-20 sm:h-24 text-sm sm:text-base resize-none"
                    placeholder="Additional notes or instructions for social media promotion..."
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 pt-2 sm:pt-4">
                <button
                  type="submit"
                  className="w-full sm:w-auto bg-gradient-to-r from-red-600 to-red-700 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-xl font-semibold hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-lg text-sm sm:text-base"
                >
                  Add Page
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="w-full sm:w-auto bg-gray-100 text-gray-700 px-6 sm:px-8 py-2 sm:py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-200 text-sm sm:text-base"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Share Modal */}
        {showShareModal && (
          <ShareModal
            website={website}
            onClose={() => setShowShareModal(false)}
          />
        )}

        {/* Enhanced Pages Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {website.pages?.map((page, index) => (
            <motion.div
              key={page.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="bg-white rounded-xl lg:rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 border border-gray-200 group"
            >
              <div className="p-4 sm:p-6">
                <div className="flex items-start justify-between mb-3 sm:mb-4">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-red-100 to-red-200 rounded-xl flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={FiFileText} className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
                    </div>
                    {page.social_media_created && (
                      <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-br from-green-100 to-green-200 rounded-lg flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={FiShare2} className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-1 sm:space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <button
                      onClick={() => navigate(`/admin/website/${id}/page/${page.id}`)}
                      className="p-1 sm:p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiEdit2} className="w-3 h-3 sm:w-4 sm:h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(page.id)}
                      className="p-1 sm:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiTrash2} className="w-3 h-3 sm:w-4 sm:h-4" />
                    </button>
                  </div>
                </div>

                <h3 className="font-bold text-gray-900 text-base sm:text-lg mb-2 line-clamp-2">{page.title}</h3>
                <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-3">{page.content}</p>

                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <SafeIcon icon={FiSearch} className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500" />
                    <span className="text-xs sm:text-sm font-medium text-gray-700">
                      {page.keyPhrases?.length || 0} key phrases
                    </span>
                  </div>
                  {page.ranking && (
                    <span className="bg-gradient-to-r from-green-100 to-green-200 text-green-800 text-xs px-2 sm:px-3 py-1 rounded-full font-semibold">
                      {page.ranking}
                    </span>
                  )}
                </div>

                {page.social_media_created && (
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg sm:rounded-xl p-2 sm:p-3 mb-3 sm:mb-4">
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      <SafeIcon icon={FiShare2} className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                      <span className="text-xs sm:text-sm font-semibold text-blue-800">Social Media Ready</span>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">
                      Ready for {page.social_media_platforms?.join(', ') || 'social media'}
                    </p>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between space-y-2 sm:space-y-0 sm:space-x-3">
                  <a
                    href={page.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center space-x-1 sm:space-x-2 text-blue-600 hover:text-blue-800 transition-colors text-xs sm:text-sm"
                  >
                    <SafeIcon icon={FiExternalLink} className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="font-medium">View Page</span>
                  </a>
                  <button
                    onClick={() => navigate(`/admin/website/${id}/page/${page.id}`)}
                    className="bg-gradient-to-r from-red-600 to-red-700 text-white px-3 sm:px-4 py-1 sm:py-2 rounded-xl text-xs sm:text-sm font-semibold hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-md"
                  >
                    Edit
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {(!website.pages || website.pages.length === 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16 sm:py-20 px-4"
          >
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl lg:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <SafeIcon icon={FiFileText} className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">No pages yet</h3>
            <p className="text-gray-600 mb-6 sm:mb-8 max-w-md mx-auto text-sm sm:text-base">
              Start adding pages to track their SEO performance and create detailed reports with social media content for your clients.
            </p>
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full sm:w-auto bg-gradient-to-r from-red-600 to-red-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-lg text-sm sm:text-base"
            >
              Add First Page
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default WebsiteDetail;